import { z } from '@repo/schema'
import { GetAddressSchema, GetCountrySchema } from '@repo/schema/address'
import { GetCompanyTagsSchema, GetCompanyTagTypeSchema } from '@repo/schema/company-tag'
import {
  GetCompanyAccountSchema,
  GetCompanyAddressSchema,
  GetCompanyCountrySchema,
  GetCompanyFinancialsSchema,
  GetCompanyLatestMemberLoginSchema,
  GetCompanyProfileSchema,
  GetCompanySchema,
  GetCompanySearchResultSchema,
  GetCompanyType,
  GetCompanyXeroAttributesSchema,
  GetHaulierProfileSchema,
} from '@repo/schema/company'
import {
  GetCompanyCommentSchema,
  GetCompanyCommentWithUserSchema,
} from '@repo/schema/company-comment'
import {
  GetCompanyDocumentInfoSchema,
  GetCompanyDocumentMediaSchema,
  GetCompanyDocumentSchema,
  GetCompanyDocumentTypeSchema,
} from '@repo/schema/company-document'
import {
  CompanyMergeInputSchema,
  CompanyMergePreviewInputSchema,
  GetCompanyDuplicateDismissalSchema,
  GetCompanyDuplicateSchema,
  GetCompanyMergePartySchema,
  GetCompanyMergePreviewSchema,
} from '@repo/schema/company-merge'
import { GetContactSchema, PatchContactSchema, PostContactSchema } from '@repo/schema/contact'
import { GetCurrencySchema } from '@repo/schema/currency'
import {
  CreateCustomerSetupFormTokenInputSchema,
  GetCustomerSetupFormSchema,
  GetCustomerSetupFormTokenSchema,
} from '@repo/schema/customer-setup-form'
import { getDataWrapperSchema } from '@repo/schema/data-wrapper'
import {
  GetCompanyLocationSchema,
  PatchCompanyLocationSchema,
  PostCompanyLocationSchema,
} from '@repo/schema/location'
import { GetHistoryEntrySchema, GetHistoryFiltersSchema } from '@repo/schema/history'
import { GetPaginationSchema } from '@repo/schema/pagination'
import { GetCompanyPaymentTermsSchema } from '@repo/schema/payment-terms'
import {
  CreateCompanyCustomerRiskProfileBodySchema,
  GetCustomerRiskProfileSchema,
} from '@repo/schema/risk-profile'
import { GetRoleSchema } from '@repo/schema/role'
import { GetSourceSchema } from '@repo/schema/source'
import { GetStatusSchema } from '@repo/schema/status'
import { GetLastAuthSchema, GetUserSchema } from '@repo/schema/user'
import { UpFetch } from '@repo/upfetch'

import { nextPage } from '../../lib/next-page'
import { historyListInputSchema, historyListParams } from '../../lib/history'
import { createProcedure } from '../../lib/procedure'
import { GetOrganisationSchema } from '@repo/schema/organisation'
import {
  addPhoneWithCountryValidation,
  createPhoneNumberFieldSchema,
  OptionalPhoneCountryCodeSchema,
} from '@repo/schema/phone'

export const createCompaniesRoute = (upFetch: UpFetch) => {
  const p = createProcedure(upFetch)
  const orgHeaderInputSchema = z.object({
    org_id: z.number().optional(),
  })
  const setupFormSortDirectionSchema = z.enum(['asc', 'desc'])
  const setupFormTokenListInputSchema = z
    .object({
      'filter[company_id]': z.number().int().positive().optional(),
      'filter[organisation_id]': z.number().int().positive().optional(),
      'filter[usable]': z.boolean().optional(),
      page: z.number().int().positive().optional(),
      perPage: z.number().int().positive().optional(),
      search: z.string().optional(),
      'sort[created_at]': setupFormSortDirectionSchema.optional(),
      'sort[expires_at]': setupFormSortDirectionSchema.optional(),
      'sort[updated_at]': setupFormSortDirectionSchema.optional(),
      'sort[used_at]': setupFormSortDirectionSchema.optional(),
    })
    .optional()
  const createCompanyAddressInputSchema = z
    .object({
      address: z.string().optional(),
      address_second_line: z.string().optional(),
      city: z.string().optional(),
      country: z.string().optional(),
      postcode: z.string().optional(),
      region: z.string().optional(),
    })
    .optional()
  const createCompanyInputSchema = z.looseObject({
    address: createCompanyAddressInputSchema,
    country: z.string().optional(),
    email: z.email().optional(),
    notes: z.string().optional(),
    phone: createPhoneNumberFieldSchema().optional(),
    phone_country: OptionalPhoneCountryCodeSchema.optional(),
    registration_number: z.string().optional(),
    short_title: z.string().optional(),
    source: z.string().optional(),
    title: z.string(),
    type: z.enum(['broker', 'haulier', 'shipper']),
    vat: z.string().optional(),
    website: z.string().optional(),
  })
  const setupFormListInputSchema = z
    .object({
      'filter[city]': z.string().optional(),
      'filter[country]': z.string().optional(),
      'filter[created_at]': z.string().optional(),
      page: z.number().optional(),
      perPage: z.number().optional(),
      search: z.string().optional(),
      'sort[city]': setupFormSortDirectionSchema.optional(),
      'sort[company_name]': setupFormSortDirectionSchema.optional(),
      'sort[country]': setupFormSortDirectionSchema.optional(),
      'sort[created_at]': setupFormSortDirectionSchema.optional(),
      'sort[email]': setupFormSortDirectionSchema.optional(),
      'sort[phone]': setupFormSortDirectionSchema.optional(),
      'sort[postcode]': setupFormSortDirectionSchema.optional(),
      'sort[registration_number]': setupFormSortDirectionSchema.optional(),
      'sort[updated_at]': setupFormSortDirectionSchema.optional(),
      'sort[user_email]': setupFormSortDirectionSchema.optional(),
      'sort[user_name]': setupFormSortDirectionSchema.optional(),
      'sort[user_phone]': setupFormSortDirectionSchema.optional(),
      'sort[vat_number]': setupFormSortDirectionSchema.optional(),
    })
    .optional()
  const companyDetailsProfileSchema = GetCompanyProfileSchema.extend({
    ...GetHaulierProfileSchema.shape,
    haulier: GetHaulierProfileSchema.partial().nullish(),
    shipper: GetCompanyProfileSchema.partial().nullish(),
  }).partial()

  return {
    // DELETE /companies/tag-types/:slug: archives a custom type; companies that have it keep it.
    // System types answer 422.
    archiveTagType: p
      .path(z.object({ slug: z.string() }), p => ['companies', 'tag-types', p.slug])
      .mutation(({ path, upFetch }) => upFetch(path, { method: 'DELETE' })),

    comments: {
      // POST /companies/comments
      create: p
        .path('companies', 'comments')
        .input(
          z.object({
            company_id: z.number(),
            content: z.string(),
          }),
        )
        .mutation(({ input, path, upFetch }) =>
          upFetch(path, {
            body: input,
            method: 'POST',
            schema: getDataWrapperSchema(GetCompanyCommentSchema),
          }),
        ),

      // DELETE /companies/comments/:id
      delete: p
        .path(z.object({ id: z.number() }), p => ['companies', 'comments', p.id])
        .mutation(({ path, upFetch }) => upFetch(path, { method: 'DELETE' })),

      // GET /companies/:id/comments (infinite, newest first)
      infiniteListByCompany: p
        .path(z.object({ id: z.number() }), p => ['companies', p.id, 'comments'])
        .input(z.object({ perPage: z.number() }))
        .infiniteQuery(
          ({ input, page, path, upFetch }) =>
            upFetch(path, {
              params: { i: 'user', page, perPage: input.perPage, 'sort[created_at]': 'desc' },
              schema: GetPaginationSchema(GetCompanyCommentWithUserSchema),
            }),
          nextPage,
        ),

      // GET /companies/:id/comments
      listByCompany: p
        .path(z.object({ id: z.number() }), p => ['companies', p.id, 'comments'])
        .input(
          z
            .object({
              page: z.number().optional(),
              perPage: z.number().optional(),
            })
            .optional(),
        )
        .query(({ input, path, upFetch }) =>
          upFetch(path, {
            params: {
              i: 'user',
              page: input?.page,
              perPage: input?.perPage,
              'sort[created_at]': 'desc',
            },
            schema: GetPaginationSchema(GetCompanyCommentWithUserSchema),
          }),
        ),

      // PATCH /companies/comments/:id
      update: p
        .path(z.object({ id: z.number() }), p => ['companies', 'comments', p.id])
        .input(
          z.object({
            content: z.string(),
          }),
        )
        .mutation(({ input, path, upFetch }) =>
          upFetch(path, {
            body: input,
            method: 'PATCH',
            schema: getDataWrapperSchema(GetCompanyCommentSchema),
          }),
        ),
    },

    contacts: {
      // POST /companies/:id/contacts
      create: p
        .path(z.object({ id: z.number() }), p => ['companies', p.id, 'contacts'])
        .input(PostContactSchema.extend({ org_id: z.number().optional() }))
        .organisation('org_id')
        .mutation(({ input, path, upFetch }) =>
          upFetch(path, {
            body: input,
            method: 'POST',
            schema: getDataWrapperSchema(GetContactSchema),
          }),
        ),

      // DELETE /companies/:companyId/contacts/:id
      delete: p
        .path(z.object({ companyId: z.number(), id: z.number() }), p => [
          'companies',
          p.companyId,
          'contacts',
          p.id,
        ])
        .mutation(({ path, upFetch }) => upFetch(path, { method: 'DELETE' })),

      // GET /companies/:companyId/contacts/:id
      get: p
        .path(z.object({ companyId: z.number(), id: z.number() }), p => [
          'companies',
          p.companyId,
          'contacts',
          p.id,
        ])
        .query(({ path, upFetch }) =>
          upFetch(path, { schema: getDataWrapperSchema(GetContactSchema) }),
        ),

      // GET /companies/:id/contacts
      list: p
        .path(z.object({ id: z.number() }), p => ['companies', p.id, 'contacts'])
        .input(orgHeaderInputSchema)
        .organisation('org_id')
        .query(({ path, upFetch }) =>
          upFetch(path, {
            schema: getDataWrapperSchema(z.array(GetContactSchema)),
          }),
        ),

      // PATCH /companies/:companyId/contacts/:id
      update: p
        .path(z.object({ companyId: z.number(), id: z.number() }), p => [
          'companies',
          p.companyId,
          'contacts',
          p.id,
        ])
        .input(PatchContactSchema)
        .mutation(({ input, path, upFetch }) =>
          upFetch(path, {
            body: input,
            method: 'PATCH',
            schema: getDataWrapperSchema(GetContactSchema),
          }),
        ),
    },

    createBroker: p
      .path('companies', 'brokers')
      .input(
        createCompanyInputSchema
          .extend({
            organisation_id: z.number().optional(),
            type: z.literal('broker'),
          })
          .superRefine((data, ctx) => {
            addPhoneWithCountryValidation(ctx, data.phone, data.phone_country)
          }),
      )
      .organisation('organisation_id')
      .mutation(({ input, organisationId, path, upFetch }) =>
        upFetch(path, {
          body: { ...input, organisation_id: organisationId },
          method: 'POST',
          schema: getDataWrapperSchema(
            GetCompanySchema.pick({
              id: true,
              title: true,
            }).extend({
              type: GetCompanyType.nullish(),
            }),
          ),
        }),
      ),

    createCompanyShipper: p
      .path('companies', 'shippers')
      .input(
        createCompanyInputSchema
          .extend({
            organisation_id: z.number().optional(),
            type: z.literal('shipper'),
          })
          .superRefine((data, ctx) => {
            addPhoneWithCountryValidation(ctx, data.phone, data.phone_country)
          }),
      )
      .organisation('organisation_id')
      .mutation(({ input, organisationId, path, upFetch }) =>
        upFetch(path, {
          body: { ...input, organisation_id: organisationId },
          method: 'POST',
          schema: getDataWrapperSchema(
            GetCompanySchema.pick({
              id: true,
              title: true,
            }).extend({
              type: GetCompanyType.nullish(),
            }),
          ),
        }),
      ),

    createShipper: p
      .path('shippers', 'company')
      .input(
        createCompanyInputSchema
          .extend({
            organisation_id: z.number().optional(),
            type: z.literal('shipper'),
          })
          .superRefine((data, ctx) => {
            addPhoneWithCountryValidation(ctx, data.phone, data.phone_country)
          }),
      )
      .organisation('organisation_id')
      .mutation(({ input, organisationId, path, upFetch }) =>
        upFetch(path, {
          body: { ...input, organisation_id: organisationId },
          method: 'POST',
          schema: getDataWrapperSchema(
            GetCompanySchema.pick({
              id: true,
              title: true,
            }).extend({
              type: GetCompanyType.nullish(),
            }),
          ),
        }),
      ),

    // PATCH /companies/:id/credit-limit
    creditLimit: {
      update: p
        .path(z.object({ id: z.number() }), p => ['companies', p.id, 'credit-limit'])
        .input(z.object({ credit_limit: z.number().int().min(0) }))
        .mutation(({ input, path, upFetch }) =>
          upFetch(path, {
            body: input,
            method: 'PATCH',
            schema: getDataWrapperSchema(GetCompanyXeroAttributesSchema),
          }),
        ),
    },

    customer: {
      riskProfile: {
        create: p
          .path(z.object({ id: z.number() }), p => ['companies', 'customers', p.id, 'risk-profile'])
          .input(CreateCompanyCustomerRiskProfileBodySchema)
          .mutation(({ input, path, upFetch }) =>
            upFetch(path, {
              body: input,
              method: 'POST',
              schema: getDataWrapperSchema(GetCustomerRiskProfileSchema),
            }),
          ),
        update: p
          .path(z.object({ id: z.number() }), p => ['companies', 'customers', p.id, 'risk-profile'])
          .input(CreateCompanyCustomerRiskProfileBodySchema)
          .mutation(({ input, path, upFetch }) =>
            upFetch(path, {
              body: input,
              method: 'PATCH',
              schema: getDataWrapperSchema(GetCustomerRiskProfileSchema),
            }),
          ),
      },
      setupForm: {
        delete: p
          .path(z.object({ id: z.number() }), p => ['setup-forms', 'customers', p.id])
          .mutation(({ path, upFetch }) => upFetch(path, { method: 'DELETE' })),
        list: p
          .path('setup-forms', 'customers')
          .input(setupFormListInputSchema)
          .query(({ input, path, upFetch }) =>
            upFetch(path, {
              params: input,
              schema: GetPaginationSchema(GetCustomerSetupFormSchema),
            }),
          ),
        tokens: {
          create: p
            .path('setup-forms', 'customers', 'tokens')
            .input(CreateCustomerSetupFormTokenInputSchema)
            .mutation(({ input, path, upFetch }) =>
              upFetch(path, {
                body: input,
                method: 'POST',
                schema: getDataWrapperSchema(GetCustomerSetupFormTokenSchema),
              }),
            ),
          delete: p
            .path(z.object({ id: z.number() }), p => ['setup-forms', 'customers', 'tokens', p.id])
            .mutation(({ path, upFetch }) => upFetch(path, { method: 'DELETE' })),
          list: p
            .path('setup-forms', 'customers', 'tokens')
            .input(setupFormTokenListInputSchema)
            .query(({ input, path, upFetch }) =>
              upFetch(path, {
                params: {
                  ...input,
                  i: 'createdBy',
                },
                schema: GetPaginationSchema(GetCustomerSetupFormTokenSchema),
              }),
            ),
        },
      },
    },

    // DELETE companies/:id
    delete: p
      .path(z.object({ id: z.number() }), p => ['companies', p.id])
      .mutation(p => upFetch(p.path, { method: 'DELETE' })),

    documents: {
      // DELETE /companies/:companyId/documents/:id
      delete: p
        .path(z.object({ companyId: z.number(), id: z.number() }), p => [
          'companies',
          p.companyId,
          'documents',
          p.id,
        ])
        .mutation(({ path, upFetch }) => upFetch(path, { method: 'DELETE' })),

      // GET /companies/:companyId/documents/:id/downloads
      getDownloadUrl: p
        .path(z.object({ companyId: z.number(), id: z.number() }), p => [
          'companies',
          p.companyId,
          'documents',
          p.id,
          'downloads',
        ])
        .query(({ path, upFetch }) =>
          upFetch(path, {
            schema: z.object({ url: z.url() }),
          }),
        ),

      // GET /companies/:id/documents
      list: p
        .path(z.object({ id: z.number() }), p => ['companies', p.id, 'documents'])
        .query(({ path, upFetch }) =>
          upFetch(path, {
            params: {
              i: ['media', 'type', 'user', 'document_info'].join('|'),
              perPage: 100,
              'sort[created_at]': 'desc',
            },
            schema: GetPaginationSchema(
              GetCompanyDocumentSchema.extend({
                document_info: GetCompanyDocumentInfoSchema.nullish(),
                media: GetCompanyDocumentMediaSchema,
                type: GetCompanyDocumentTypeSchema,
                user: GetUserSchema.nullish(),
              }),
            ),
          }),
        ),

      // GET /companies/documents/types
      listTypes: p.path('companies', 'documents', 'types').query(({ path, upFetch }) =>
        upFetch(path, {
          schema: getDataWrapperSchema(z.array(GetCompanyDocumentTypeSchema)),
        }),
      ),

      // POST /companies/:id/documents  (multipart)
      upload: p
        .path(z.object({ id: z.number() }), p => ['companies', p.id, 'documents'])
        .input(
          z.object({
            expired_at: z.string().optional(),
            file: z.instanceof(File),
            insurance_expired_at: z.string().optional(),
            liability_type: z.enum(['employee', 'public']).optional(),
            total_sum: z.string().optional(),
            type: z.string(),
          }),
        )
        .mutation(({ input, path, upFetch }) => {
          const formData = new FormData()
          formData.append('type', input.type)
          formData.append('files[]', input.file)
          if (input.expired_at) formData.append('expired_at', input.expired_at)
          if (input.liability_type) formData.append('liability_type', input.liability_type)
          if (input.total_sum) formData.append('total_sum', input.total_sum)
          if (input.insurance_expired_at)
            formData.append('insurance_expired_at', input.insurance_expired_at)
          return upFetch(path, { body: formData, method: 'POST' })
        }),
    },

    // GET /companies/:id (with details - no includes)
    getDetails: p
      .path(z.object({ id: z.number() }), p => ['companies', p.id])
      .query(({ path, upFetch }) =>
        upFetch(path, {
          params: {
            i: [
              'patron.avatar',
              'payment_terms',
              'source',
              'default_currency',
              'patron.avatar',
              'profile.pod_requirement',
              'financials',
              'latest_member_login',
              'organisation',
              'xero_attributes',
              'risk_profile',
              'general_contact',
              'phone_country',
            ].join('|'),
          },
          schema: getDataWrapperSchema(
            GetCompanySchema.extend({
              account: GetCompanyAccountSchema,
              address: GetCompanyAddressSchema,
              country: GetCompanyCountrySchema.nullish(),
              default_currency: GetCurrencySchema.nullish(),
              financials: GetCompanyFinancialsSchema.extend({
                currency: GetCurrencySchema,
              })
                .array()
                .transform(data => data.find(entry => entry.currency.alias === 'gbp')),
              general_contact: GetContactSchema.nullish(),
              latest_member_login: GetCompanyLatestMemberLoginSchema.nullish(),
              organisation: GetOrganisationSchema,
              patron: GetUserSchema.nullish(),
              payment_terms: GetCompanyPaymentTermsSchema.nullish(),
              profile: z.unknown().array().or(companyDetailsProfileSchema),
              risk_profile: GetCustomerRiskProfileSchema.nullish(),
              source: GetSourceSchema.nullish(),
              // NOTE: Other companies have type 'null'
              type: GetCompanyType.nullish(),
              verified_at: z.coerce.date().nullish(),
              xero_attributes: GetCompanyXeroAttributesSchema.nullish(),
            }),
          ),
        }),
      ),

    // GET /companies/:id/members
    getMembers: p
      .path(z.object({ id: z.number() }), p => ['companies', p.id, 'members'])
      .input(z.record(z.string(), z.union([z.string(), z.number()])).optional())
      .query(({ input, path, upFetch }) =>
        upFetch(path, {
          params: {
            i: 'user.avatar|user.counters|user.last_auth|user.role|user.status|user.company|user.company.type',
            ...input,
          },
          schema: GetPaginationSchema(
            z
              .object({
                user: GetUserSchema.extend({
                  company: GetCompanySchema.extend({
                    type: GetCompanyType,
                  }),
                  counters: z.object({
                    login_via_token: z.number(),
                  }),
                  last_auth: GetLastAuthSchema.nullish(),
                  role: GetRoleSchema,
                  source: z.string().nullish(),
                  status: GetStatusSchema,
                }),
              })
              .transform(data => data.user),
          ),
        }),
      ),

    getSimpleDetails: p
      .path(z.object({ id: z.number() }), p => ['companies', p.id])
      .input(orgHeaderInputSchema)
      .organisation('org_id')
      .query(({ path, upFetch }) =>
        upFetch(path, {
          schema: getDataWrapperSchema(GetCompanySchema),
        }),
      ),

    history: {
      // GET /companies/:id/history/filters
      filters: p
        .path(z.object({ id: z.number() }), p => ['companies', p.id, 'history', 'filters'])
        .query(({ path, upFetch }) =>
          upFetch(path, {
            schema: getDataWrapperSchema(GetHistoryFiltersSchema),
          }),
        ),

      // GET /companies/:id/history
      list: p
        .path(z.object({ id: z.number() }), p => ['companies', p.id, 'history'])
        .input(historyListInputSchema)
        .infiniteQuery(
          ({ input, page, path, upFetch }) =>
            upFetch(path, {
              params: historyListParams(input, page),
              schema: GetPaginationSchema(GetHistoryEntrySchema),
            }),
          nextPage,
        ),
    },

    list: p
      .path('companies')
      .input(
        z.object({
          'filter[organisation_id]': z.number().int().positive().optional(),
          'filter[type]': z.string().optional(),
          'filter[vat]': z.string().optional(),
          i: z.string().optional(),
          org_id: z.number().optional(),
          perPage: z.number().default(10),
          search: z.string().optional(),
        }),
      )
      .organisation('org_id')
      .query(({ input, path, upFetch }) => {
        const { i, ...params } = input

        return upFetch(path, {
          params: {
            i: i ? `account|${i}` : 'account',
            ...params,
          },
          schema: GetPaginationSchema(
            GetCompanySchema.extend({
              account: z.object({
                vat: z.string().nullish(),
              }),
              address: GetAddressSchema,
              country: GetCountrySchema.nullish(),
              organisation: GetOrganisationSchema.nullish(),
              risk_profile: GetCustomerRiskProfileSchema.nullish(),
              type: GetCompanyType.nullish(),
              xero_attributes: GetCompanyXeroAttributesSchema.nullish(),
            }),
          ),
        })
      }),

    // GET /companies/:filter (paginated list by company type)
    listByType: p
      .path(z.object({ filter: z.enum(['broker', 'haulier', 'other', 'shipper']) }), p => [
        'companies',
        p.filter,
      ])
      .input(z.record(z.string(), z.union([z.number(), z.string()])))
      .query(({ input, path, upFetch }) =>
        upFetch(path, {
          params: {
            ...input,
            i: [
              'updated_at',
              'profile',
              'financials',
              'organisation',
              'xero_attributes',
              'latest_member_login',
              'risk_profile',
              'payment_terms',
              'country',
            ].join('|'),
          },
          schema: GetPaginationSchema(
            GetCompanySchema.extend({
              address: GetCompanyAddressSchema.nullish(),
              avatar: z
                .object({
                  url: z.string(),
                })
                .nullish(),
              country: GetCompanyCountrySchema.nullish(),
              created_at: z.coerce.date().nullish(),
              email: z.string().nullish(),
              financials: GetCompanyFinancialsSchema.pick({
                credit_limit: true,
              })
                .extend({ currency: GetCurrencySchema })
                .array()
                .transform(data => data.find(entry => entry.currency.alias === 'gbp'))
                .nullish(),
              latest_member_login: GetCompanyLatestMemberLoginSchema.nullish(),
              organisation: GetOrganisationSchema.nullish(),
              patron: GetUserSchema.nullish(),
              payment_terms: GetCompanyPaymentTermsSchema.nullish(),
              profile: z
                .union([
                  z
                    .object({
                      haulier: z
                        .object({
                          uk_region_preferences: z.array(z.string()).nullish(),
                        })
                        .nullish(),
                      shipper: z
                        .object({
                          uk_region_preferences: z.array(z.string()).nullish(),
                        })
                        .nullish(),
                      uk_region_preferences: z.array(z.string()).nullish(),
                    })
                    .nullish(),
                  z
                    .unknown()
                    .array()
                    .transform(() => null),
                ])
                .transform(value => value ?? null),
              risk_profile: GetCustomerRiskProfileSchema.nullish(),
              slug: z.string(),
              // Left out for users without companies:tag-types:view
              tags: GetCompanyTagsSchema.nullish(),
              type: z
                .object({
                  alias: z.string(),
                })
                .nullish(),
              updated_at: z.coerce.date().nullish(),
              xero_attributes: GetCompanyXeroAttributesSchema.pick({
                overdue_outstanding_balance: true,
                payable_balance: true,
                xero_contact_url: true,
              }).nullish(),
            }),
          ),
        }),
      ),

    locations: {
      // POST /companies/:id/locations
      create: p
        .path(z.object({ id: z.number() }), p => ['companies', p.id, 'locations'])
        .input(PostCompanyLocationSchema.extend({ org_id: z.number().optional() }))
        .organisation('org_id')
        .mutation(({ input, path, upFetch }) =>
          upFetch(path, {
            body: input,
            method: 'POST',
            schema: getDataWrapperSchema(GetCompanyLocationSchema),
          }),
        ),

      // DELETE /companies/:companyId/locations/:id
      delete: p
        .path(z.object({ companyId: z.number(), id: z.number() }), p => [
          'companies',
          p.companyId,
          'locations',
          p.id,
        ])
        .mutation(({ path, upFetch }) => upFetch(path, { method: 'DELETE' })),

      // GET /companies/:companyId/locations/:id
      get: p
        .path(z.object({ companyId: z.number(), id: z.number() }), p => [
          'companies',
          p.companyId,
          'locations',
          p.id,
        ])
        .query(({ path, upFetch }) =>
          upFetch(path, { schema: getDataWrapperSchema(GetCompanyLocationSchema) }),
        ),

      // GET /companies/:id/locations
      list: p
        .path(z.object({ id: z.number() }), p => ['companies', p.id, 'locations'])
        .input(
          orgHeaderInputSchema.extend({
            page: z.number().optional(),
            perPage: z.number().optional(),
            search: z.string().optional(),
            'sort[created_at]': z.enum(['asc', 'desc']).optional(),
            'sort[id]': z.enum(['asc', 'desc']).optional(),
            'sort[name]': z.enum(['asc', 'desc']).optional(),
            'sort[updated_at]': z.enum(['asc', 'desc']).optional(),
          }),
        )
        .organisation('org_id')
        .query(({ input, path, upFetch }) =>
          upFetch(path, {
            params: input,
            schema: GetPaginationSchema(GetCompanyLocationSchema),
          }),
        ),

      // PATCH /companies/:companyId/locations/:id
      update: p
        .path(z.object({ companyId: z.number(), id: z.number() }), p => [
          'companies',
          p.companyId,
          'locations',
          p.id,
        ])
        .input(PatchCompanyLocationSchema)
        .mutation(({ input, path, upFetch }) =>
          upFetch(path, {
            body: input,
            method: 'PATCH',
            schema: getDataWrapperSchema(GetCompanyLocationSchema),
          }),
        ),
    },

    // In a merge, the route company is the duplicate (source); `target_id` is the company that survives.
    merge: {
      // Likely duplicates of the route company, suggested for a merge.
      duplicates: {
        // POST /companies/:id/duplicates/:duplicateId/dismissal — marks the pair as not duplicates.
        dismiss: p
          .path(z.object({ duplicateId: z.number(), id: z.number() }), params => [
            'companies',
            params.id,
            'duplicates',
            params.duplicateId,
            'dismissal',
          ])
          .mutation(({ path, upFetch }) =>
            upFetch(path, {
              method: 'POST',
              schema: getDataWrapperSchema(GetCompanyDuplicateDismissalSchema),
            }),
          ),

        // GET /companies/:id/duplicates
        list: p
          .path(z.object({ id: z.number() }), params => ['companies', params.id, 'duplicates'])
          .query(({ path, upFetch }) =>
            upFetch(path, {
              schema: getDataWrapperSchema(GetCompanyDuplicateSchema.array()),
            }),
          ),
      },

      // POST /companies/:id/merge
      execute: p
        .path(z.object({ id: z.number() }), params => ['companies', params.id, 'merge'])
        .input(CompanyMergeInputSchema)
        .mutation(({ input, path, upFetch }) =>
          upFetch(path, {
            body: input,
            method: 'POST',
            schema: getDataWrapperSchema(GetCompanySchema),
          }),
        ),

      // GET /companies/:id/merge/party
      party: p
        .path(z.object({ id: z.number() }), params => ['companies', params.id, 'merge', 'party'])
        .query(({ path, upFetch }) =>
          upFetch(path, {
            schema: getDataWrapperSchema(GetCompanyMergePartySchema),
          }),
        ),

      // POST /companies/:id/merge/preview — writes nothing, so it is read as a query.
      preview: p
        .path(z.object({ id: z.number() }), params => ['companies', params.id, 'merge', 'preview'])
        .input(CompanyMergePreviewInputSchema)
        .query(({ input, path, upFetch }) =>
          upFetch(path, {
            body: input,
            method: 'POST',
            schema: getDataWrapperSchema(GetCompanyMergePreviewSchema),
          }),
        ),
    },

    requirements: {
      pod: {
        // GET /companies/requirements/pod
        list: p.path('companies', 'requirements', 'pod').query(({ path, upFetch }) =>
          upFetch(path, {
            schema: getDataWrapperSchema(
              z.array(
                z.object({
                  alias: z.string(),
                  id: z.number(),
                  title: z.string(),
                }),
              ),
            ),
          }),
        ),
      },
    },

    // GET /companies/search (ranked by the API's search index: 3PLs, then usage, then title)
    search: p
      .path('companies', 'search')
      .input(
        z.object({
          // The API rejects an empty filter with 409, so a filter is either set or omitted.
          'filter[is_marketplace]': z.boolean().optional(),
          'filter[organisation_id]': z.number().int().positive().optional(),
          'filter[type]': z.string().min(1).optional(),
          org_id: z.number().optional(),
          page: z.number().int().positive().optional(),
          perPage: z.number().int().min(1).max(50).default(20),
          search: z.string().optional(),
        }),
      )
      .organisation('org_id')
      .query(({ input, path, upFetch }) =>
        upFetch(path, {
          params: input,
          schema: GetPaginationSchema(GetCompanySearchResultSchema),
        }),
      ),

    // Every change answers with the company's whole tag list, so a VIP / Blacklisted swap shows up.
    tags: {
      // POST /companies/:companyId/tags
      add: p
        .path(z.object({ companyId: z.number() }), p => ['companies', p.companyId, 'tags'])
        .input(z.object({ tag_slug: z.string() }))
        .mutation(({ input, path, upFetch }) =>
          upFetch(path, {
            body: input,
            method: 'POST',
            schema: getDataWrapperSchema(GetCompanyTagsSchema),
          }),
        ),

      // POST /companies/:companyId/tags/custom: creates the type, or reactivates an archived one
      // of that name, and tags the company
      createCustom: p
        .path(z.object({ companyId: z.number() }), p => [
          'companies',
          p.companyId,
          'tags',
          'custom',
        ])
        .input(z.object({ description: z.string().nullish(), name: z.string() }))
        .mutation(({ input, path, upFetch }) =>
          upFetch(path, {
            body: input,
            method: 'POST',
            schema: getDataWrapperSchema(GetCompanyTagsSchema),
          }),
        ),

      // GET /companies/:companyId/tags (needs companies:tag-types:view)
      list: p
        .path(z.object({ companyId: z.number() }), p => ['companies', p.companyId, 'tags'])
        .query(({ path, upFetch }) =>
          upFetch(path, { schema: getDataWrapperSchema(GetCompanyTagsSchema) }),
        ),

      // DELETE /companies/:companyId/tags
      remove: p
        .path(z.object({ companyId: z.number() }), p => ['companies', p.companyId, 'tags'])
        .input(z.object({ tag_slug: z.string() }))
        .mutation(({ input, path, upFetch }) =>
          upFetch(path, {
            body: input,
            method: 'DELETE',
            schema: getDataWrapperSchema(GetCompanyTagsSchema),
          }),
        ),
    },

    // GET /companies/tag-types: active types only, sorted by order (needs companies:tag-types:view)
    tagTypes: p
      .path('companies', 'tag-types')
      .query(({ path, upFetch }) =>
        upFetch(path, { schema: getDataWrapperSchema(GetCompanyTagTypeSchema.array()) }),
      ),

    // PATCH /companies/brokers/:id
    updateBroker: p
      .path(z.object({ id: z.number() }), p => ['companies', 'brokers', p.id])
      .input(z.record(z.string(), z.unknown()))
      .mutation(({ input, path, upFetch }) =>
        upFetch(path, {
          body: input,
          method: 'PATCH',
        }),
      ),

    // PATCH /companies/hauliers/:id
    updateHaulier: p
      .path(z.object({ id: z.number() }), p => ['companies', 'hauliers', p.id])
      .input(z.record(z.string(), z.unknown()))
      .mutation(({ input, path, upFetch }) =>
        upFetch(path, {
          body: input,
          method: 'PATCH',
        }),
      ),
    // PATCH /companies/shippers/:id
    updateShipper: p
      .path(z.object({ id: z.number() }), p => ['companies', 'shippers', p.id])
      .input(z.record(z.string(), z.unknown()))
      .mutation(({ input, path, upFetch }) =>
        upFetch(path, {
          body: input,
          method: 'PATCH',
        }),
      ),
    // PATCH /companies/:id/type
    updateType: p
      .path(z.object({ id: z.number() }), p => ['companies', p.id, 'type'])
      .input(z.object({ type: z.enum(['shipper', 'haulier', 'broker', 'other']) }))
      .mutation(({ input, path, upFetch }) =>
        upFetch(path, {
          body: input,
          method: 'PATCH',
          schema: getDataWrapperSchema(
            GetCompanySchema.pick({ id: true, title: true }).extend({
              type: GetCompanyType.nullish(),
            }),
          ),
        }),
      ),
  }
}
